import { useCallback } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { Alert } from '@cmsgov/ds-medicare-gov'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { MspSortForm } from 'models/claims'
import { logAriaMessage } from 'redux/reducers/aria-log'
import { MspCaseResults } from './MspCaseResults'
import { MspAdditionalInfo } from './MspAdditionalInfo'
import { MspCaseSort } from './MspCaseSort'
import BsiButton from './BsiButton'
import { Status } from 'redux/helpers/claimCase'
import {
  getSearchResults,
  getSearchResultsTotal,
  getSortDirection,
  getSortType,
  getStatus,
  showAllSearchResults,
  showMoreSearchResults,
  sortCases
} from 'redux/reducers/msp'
import { useFetchOifCookiesOnLoad } from 'containers/Claims/hooks'

export const MspSpanishNote = () => (
  <p>
    <strong>Atención:</strong> El MSPRP no está disponible en español. Si desea
    acceder a esta página en ingles, haga clic en "Vaya a MSPRP" botón.
  </p>
)

export const MspContent = () => {
  const classType = 'msp'
  const { t, i18n } = useTranslation(classType)
  const dispatch = useDispatch()
  const status = useSelector(getStatus)
  const oifCookieLoadState = useFetchOifCookiesOnLoad(status)
  const caseResults = useSelector(getSearchResults)
  const totalCaseResults = useSelector(getSearchResultsTotal)
  const sortDirection = useSelector(getSortDirection)
  const sortType = useSelector(getSortType)
  const isOifError = oifCookieLoadState === 'error'
  const isLoading = (status === Status.LOADING || oifCookieLoadState === 'loading')
  const isError = status === Status.ERROR
  const isFinished =
    status === Status.FINISHED && oifCookieLoadState !== 'loading'

  const handleSubmit: SubmitHandler<MspSortForm> = useCallback(
    (formValues: MspSortForm) => {
      dispatch(
        sortCases({
          sortType: formValues.sortType,
          sortDirection: formValues.sortDirection
        })
      )
      dispatch(logAriaMessage(t('sortComplete')))
    },
    [dispatch, t]
  )

  const handleShowMoreSearchResults = () => {
    dispatch(showMoreSearchResults())
  }

  const handleShowAllSearchResults = () => {
    dispatch(showAllSearchResults())
  }

  return (
    <>
      <div className="ds-l-row">
        <div className="ds-l-col--12">
          {(isError || isOifError) && (
            <Alert
              className="ds-u-margin-bottom--3"
              variation="warn"
              role="alert"
            >
              {t('summary.errorAlert')}
            </Alert>
          )}

          <p>{t('summary.description')}</p>
          {i18n.resolvedLanguage === 'es' && <MspSpanishNote />}
          <Alert hideIcon className="ds-u-margin-y--3">
            {t('summary.popupAlert')}
          </Alert>
          {!isOifError && (
            <div>
              <BsiButton
                data-testid="go-to-msprp"
                className="ds-c-button ds-c-button--solid ds-c-button--alternate my-u-mb--16"
                aria-label={t('navigation.goToMsprp')}
                siteActivityProps={{
                  core: {
                    component: 'Medicare Secondary Payer',
                    elementId: 'msp_gotomsprp_click',
                    coreClickData: {
                      linkText: t('navigation.goToMsprp'),
                      linkUrl:
                        process.env.REACT_APP_MSPRP_BSI_URL ||
                        'https://bsi.medicare.gov/fed/idp/initiatesso?providerid=MSPRP_PRO'
                    }
                  }
                }}
              >
                {t('navigation.goToMsprp')}
              </BsiButton>
            </div>
          )}
        </div>
      </div>
      {isLoading && <MySpinner />}
      {!isLoading && !isError && (
        <>
          <div>
            <MspCaseSort
              classType={classType}
              sortDirection={sortDirection}
              sortType={sortType}
              onSubmit={handleSubmit}
              className="ds-u-border-top--1 ds-u-padding-top--3"
            />
          </div>
          <div className="ds-u-margin-top--3">
            <MspCaseResults
              classType={classType}
              isLoading={isLoading}
              isError={isError}
              isFinished={isFinished}
              results={caseResults}
              totalCaseResults={totalCaseResults}
              onShowMoreSearchResults={handleShowMoreSearchResults}
              onShowAllSearchResults={handleShowAllSearchResults}
              className="ds-u-border-top--1 ds-u-padding-top--3"
            />
          </div>
        </>
      )}
      <div className="ds-l-row ds-u-margin-top--7">
        <div className="ds-l-col--12">
          <MspAdditionalInfo />
        </div>
      </div>
    </>
  )
}
